import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogConfig } from '@angular/material/dialog/dialog-config';
import { MatDialogRef } from '@angular/material/dialog/dialog-ref';
import { CacheService } from '@app/services/cache.service';
import { PresenterModeService } from '@app/services/presenter-mode.service';
import { SupportModalService } from '@app/support-request/support-modal-component/support-modal.service';
import { PermissionService } from '@core/services/permission/permission.service';
import { ApplyPotentialSavingsComponent } from '@modules/potential-save-modal/apply-potential-savings/apply-potential-savings.component';
import { IPotentialSavingsSteadyStateDTO } from '@modules/potential-save-modal/dto/i-potential-savings-steady-state.dto';
import {
	ICalculatePotentialSavingsParamsDTO,
	IPotentialSavingsDTO,
} from '@modules/potential-save-modal/dto/i-potential-savings.dto';
import { PotentialSaveErrorsModalComponent } from '@modules/potential-save-modal/potential-save-errors-modal/potential-save-errors-modal.component';
import { PotentialSaveModalComponent } from '@modules/potential-save-modal/potential-save-modal.component';
import { PotentialSavingsApiService } from '@modules/potential-save-modal/potential-savings-api.service';
import { PotentialSavingsCloseActions } from '@modules/potential-save-modal/potential-savings-close-actions.enum';
import {
	DEFAULT_PS_MODAL_HEIGHT,
	DEFAULT_PS_MODAL_PANEL_CLASS,
	DEFAULT_PS_MODAL_SIZES,
	DEFAULT_PS_MODAL_WIDTH,
	ERRORS_PS_DIALOG_CONTAINER,
	LG_PS_MODAL_HEIGHT,
	LG_PS_MODAL_WIDTH,
	MD_PS_MODAL_HEIGHT,
	MD_PS_MODAL_WIDTH,
	PS_MODAL_ANIMATION_DURATION,
	SM_PS_MODAL_HEIGHT,
	SM_PS_MODAL_SIZES,
	SM_PS_MODAL_WIDTH,
} from '@modules/potential-save-modal/potential-savings-modal.config';
import {
	PotentialSavingErrorType,
	PotentialSavingsViews,
} from '@modules/potential-save-modal/potential-savings-views.enum';
import { RefreshPotentialSavingsComponent } from '@modules/potential-save-modal/refresh-potential-savings/refresh-potential-savings.component';
import moment from 'moment/moment';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class PotentialSavingsService {
	private readonly commonDialogConfig: MatDialogConfig = {
		panelClass: DEFAULT_PS_MODAL_PANEL_CLASS,
		autoFocus: 'dialog',
		disableClose: true,
	};
	private dialogRef: MatDialogRef<PotentialSaveModalComponent, PotentialSavingsCloseActions>;
	private readonly _isExtended$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private readonly _isAnimationDisabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	private readonly _currentView: BehaviorSubject<PotentialSavingsViews> = new BehaviorSubject<PotentialSavingsViews>(
		PotentialSavingsViews.LOADING,
	);
	private readonly _isMonthlyState: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private readonly _potentialSavings: BehaviorSubject<IPotentialSavingsDTO | null> =
		new BehaviorSubject<IPotentialSavingsDTO | null>(null);
	private readonly _isEDP: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

	public readonly linuxSteadyState$: Observable<IPotentialSavingsSteadyStateDTO> = this.potentialSavingsApiService
		.getLinuxSteadyState()
		.pipe(shareReplay());

	public get currentView$(): Observable<PotentialSavingsViews> {
		return this._currentView.asObservable();
	}

	public get potentialSavings$(): Observable<IPotentialSavingsDTO> {
		return this._potentialSavings.asObservable().pipe(filter((potentialSavings) => !!potentialSavings));
	}

	public get isMonthlyState$(): Observable<boolean> {
		return this._isMonthlyState.asObservable();
	}

	public get isEDP$(): Observable<boolean> {
		return this._isEDP.asObservable().pipe(filter((isEDP) => isEDP !== null));
	}

	public get isExtended$(): Observable<boolean> {
		return this._isExtended$.asObservable();
	}

	public get isAnimationDisabled$(): Observable<boolean> {
		return this._isAnimationDisabled$.asObservable();
	}

	constructor(
		private dialog: MatDialog,
		private supportModalService: SupportModalService,
		private potentialSavingsApiService: PotentialSavingsApiService,
		private presenterMode: PresenterModeService,
		private permissions: PermissionService,
		private cacheService: CacheService,
	) {}

	public openModal(): void {
		this.dialogRef = this.dialog.open(PotentialSaveModalComponent, {
			...DEFAULT_PS_MODAL_SIZES,
			...this.commonDialogConfig,
		});

		this.dialogRef.afterClosed().subscribe((action) => {
			if (action === PotentialSavingsCloseActions.Apply) {
				this.dialog.open(ApplyPotentialSavingsComponent, {
					...SM_PS_MODAL_SIZES,
					...this.commonDialogConfig,
				});
			}
			if (action === PotentialSavingsCloseActions.Refresh) {
				this.dialog.open(RefreshPotentialSavingsComponent, {
					...SM_PS_MODAL_SIZES,
					...this.commonDialogConfig,
				});
			}
			if (action === PotentialSavingsCloseActions.Error) {
				this.openPotentialSavingErrorModal();
			}
			this.setIsExtended(false);
		});
	}

	public closeModal(): void {
		this.dialogRef.close();
	}

	public setIsMonthlyState(newState: boolean): void {
		this._isMonthlyState.next(newState);
	}

	public expandDialog(): void {
		this.setIsAnimationDisabled(false);
		this.setIsExtended(true);
		this.dialogRef.updateSize(`${MD_PS_MODAL_WIDTH}px`, `${MD_PS_MODAL_HEIGHT}px`);
	}

	public openTotalSavingPanel(): void {
		this.setIsAnimationDisabled(true);
		this.setIsExtended(true);
		this._currentView.next(PotentialSavingsViews.TOTAL_SAVING);
		this.dialogRef.updateSize(`${MD_PS_MODAL_WIDTH}px`, `${MD_PS_MODAL_HEIGHT}px`);
	}

	public shrinkDialog(): void {
		this.setIsAnimationDisabled(false);
		this.setIsExtended(false);
		const magicExtraDelay = 50;

		setTimeout(() => {
			this.dialogRef.updateSize(`${DEFAULT_PS_MODAL_WIDTH}px`, `${DEFAULT_PS_MODAL_HEIGHT}px`);
		}, PS_MODAL_ANIMATION_DURATION + magicExtraDelay);
	}

	public replaceModalView(view: PotentialSavingsViews): void {
		this._currentView.next(view);
		if (view === PotentialSavingsViews.RI_SP_TABLE) {
			this.dialogRef.updateSize(`${LG_PS_MODAL_WIDTH}px`, `${LG_PS_MODAL_HEIGHT}px`);
			return;
		}
		this.dialogRef.updateSize(`${MD_PS_MODAL_WIDTH}px`, `${MD_PS_MODAL_HEIGHT}px`);
	}

	public getPotentialSavingsByParams(
		params: ICalculatePotentialSavingsParamsDTO,
	): Observable<Partial<IPotentialSavingsDTO>> {
		return this.potentialSavingsApiService.getPotentialSavingsByParams(params).pipe(
			tap((response) => {
				const currentData = this._potentialSavings.getValue();
				this._potentialSavings.next({ ...currentData, ...response });
			}),
		);
	}

	public loadPotentialSavings(): Observable<IPotentialSavingsDTO> {
		this._currentView.next(PotentialSavingsViews.LOADING);
		const key = `getPotentialSavings__${this._isEDP.getValue()}`;
		let obs$: Observable<HttpResponse<IPotentialSavingsDTO>>;

		if (this.cacheService.get$(key)) {
			obs$ = this.cacheService.get$(key);
		}
		if (!this.cacheService.get$(key)) {
			obs$ = this.potentialSavingsApiService.getPotentialSavings(this._isEDP.getValue()).pipe(shareReplay());
			this.cacheService.set$(key, obs$);
		}

		return obs$.pipe(
			map((res: HttpResponse<IPotentialSavingsDTO>) => {
				if (res.status === 200) {
					const isEDP = this._isEDP.getValue() || !!res.body.isEDP;
					this.setIsMonthlyState(!isEDP);
					this.setIsEDP(isEDP);
					this._potentialSavings.next(res.body);
					this._currentView.next(PotentialSavingsViews.TOTAL_SAVING);

					return res.body;
				}
				if (res.status === 202) {
					const type: PotentialSavingErrorType = res.body.type;
					this._potentialSavings.next({ type } as IPotentialSavingsDTO);
					this._currentView.next(PotentialSavingsViews.ERROR);
					this.openPotentialSavingErrorModal();

					return { type } as IPotentialSavingsDTO;
				}
			}),
			catchError((err: HttpErrorResponse) => {
				const type: PotentialSavingErrorType = err?.error?.type || PotentialSavingErrorType.SERVER_ERROR;
				this._potentialSavings.next({ type } as IPotentialSavingsDTO);
				this._currentView.next(PotentialSavingsViews.ERROR);
				this.openPotentialSavingErrorModal();

				return of({ type } as IPotentialSavingsDTO);
			}),
		);
	}

	public toggleEDP(isEDP: boolean): Observable<IPotentialSavingsDTO> {
		this._currentView.next(PotentialSavingsViews.LOADING);
		this.setIsEDP(isEDP);

		const key = `getPotentialSavings__${isEDP}`;
		let obs$: Observable<HttpResponse<IPotentialSavingsDTO>>;

		if (this.cacheService.get$(key)) {
			obs$ = this.cacheService.get$(key);
		}
		if (!this.cacheService.get$(key)) {
			obs$ = this.potentialSavingsApiService.getPotentialSavings(isEDP).pipe(shareReplay());
			this.cacheService.set$(key, obs$);
		}

		return obs$.pipe(
			map((res: HttpResponse<IPotentialSavingsDTO>) => {
				if (res.status === 200) {
					this._potentialSavings.next(res.body);
					this.setIsMonthlyState(!isEDP);
					this._currentView.next(PotentialSavingsViews.TOTAL_SAVING);

					return res.body;
				}
				if (res.status === 202) {
					const type: PotentialSavingErrorType = res.body.type;
					this._potentialSavings.next({ type } as IPotentialSavingsDTO);
					this._currentView.next(PotentialSavingsViews.ERROR);
					this.openPotentialSavingErrorModal();

					return { type } as IPotentialSavingsDTO;
				}
			}),
			catchError((err: HttpErrorResponse) => {
				const type: PotentialSavingErrorType = err?.error?.type || PotentialSavingErrorType.SERVER_ERROR;
				this._potentialSavings.next({ type } as IPotentialSavingsDTO);
				if (err.status === 404) {
					this._currentView.next(PotentialSavingsViews.SWITCH_EDP_MODE);
					this.dialogRef.updateSize(`${DEFAULT_PS_MODAL_WIDTH}px`, `${SM_PS_MODAL_HEIGHT}px`);
				} else {
					this._currentView.next(PotentialSavingsViews.ERROR);
					this.openPotentialSavingErrorModal();
					this.setIsEDP(!isEDP);
				}

				return throwError(err);
			}),
		);
	}

	public confirmToggleEDP(switchConfirmed: boolean): Observable<any> {
		const isEDP = this._isEDP.getValue();
		if (!switchConfirmed) {
			this.setIsEDP(!isEDP);

			return of(null);
		} else {
			return this.potentialSavingsApiService.executePotentialSavingsByStrategy(isEDP);
		}
	}

	public refreshPotentialSavings(): Observable<any> {
		const isEDP = this._isEDP.getValue();
		return this.potentialSavingsApiService.executePotentialSavingsByStrategy(isEDP);
	}

	public openCostExplorerAWS(): void {
		const startDate = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
		const endDate = moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD');

		const link = this.createAwsLink(startDate, endDate);
		window.open(link, '_blank');
	}

	public isAllocationsAndLinuxTabsAllowed(): Observable<boolean> {
		return this.permissions.isPermittedWithPresenter(
			'my-organization__general__potential__savings__sp__allocations__linux',
			this.presenterMode.applyIncognito.asObservable(),
		);
	}

	private setIsEDP(newState: boolean): void {
		this._isEDP.next(newState);
	}

	private setIsAnimationDisabled(newState: boolean): void {
		this._isAnimationDisabled$.next(newState);
	}

	private setIsExtended(newState: boolean): void {
		this._isExtended$.next(newState);
	}

	private createAwsLink(startDate: string, endDate: string): string {
		return (
			'https://console.aws.amazon.com/cost-management/home?#/custom?groupBy=' +
			'PurchaseType&forecastTimeRangeOption=None&hasBlended=false&excludeTaggedResources=false&chartStyle=Stack&timeRangeOption=Custom&granularity=Monthly&reportName=' +
			'Monthly costs by service&isTemplate=true&reportType=CostUsage&hasAmortized=true&excludeDiscounts=true&usageAs=usageQuantity&excludeCategorizedResources=' +
			`false&excludeForecast=true&startDate=${startDate}&endDate=${endDate}&filter=` +
			'[{"dimension":"PurchaseType","values":["Spot Instances"],"include":false,"children":null},{"dimension":"UsageTypeGroup","values":[{"value":"EC2: Running Hours","unit":"Hrs"}],"include":true,"children":null},{"dimension":"RecordType","values":["Recurring","Refund","DiscountedUsage","SavingsPlanCoveredUsage","SavingsPlanNegation","SavingsPlanRecurringFee","Upfront","Usage"],"include":true,"children":null}]'
		);
	}

	private openPotentialSavingErrorModal(): void {
		const dialogRef: MatDialogRef<PotentialSaveErrorsModalComponent, boolean> = this.dialog.open(
			PotentialSaveErrorsModalComponent,
			{
				panelClass: ERRORS_PS_DIALOG_CONTAINER,
				autoFocus: 'dialog',
				disableClose: true,
				width: `${SM_PS_MODAL_WIDTH}px`,
				data: this._potentialSavings.getValue()?.type,
			},
		);
		dialogRef.afterClosed().subscribe((showSupportForm) => {
			this.dialogRef.close();
			if (showSupportForm) {
				this.supportModalService.showFromHost();
			}
		});
	}
}
